/** Internal affairs: the case list, a new case (also from a body cam referral), and a case with findings, notices and files. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, PeoplePicker, when, type Person } from './common.tsx';
import { FileList, UploadButton } from './WorkFiles.tsx';

type Meta = { sources: Record<string, string>; classes: Record<string, string>; statuses: Record<string, string>; findings: Record<string, string>; notices: Record<string, string> };
const STATUS_TONE: Record<string, 'neutral' | 'info' | 'warn' | 'ok'> = { open: 'warn', investigating: 'info', review: 'info', closed: 'neutral' };
const FINDING_TONE: Record<string, 'bad' | 'ok' | 'neutral'> = { sustained: 'bad', not_sustained: 'neutral', exonerated: 'ok', unfounded: 'ok', policy_failure: 'neutral' };
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

export function IaListPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/ia?status=${status}&q=${encodeURIComponent(q)}`), [status, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Internal affairs"
        title="IA cases"
        sub="Only IA and the investigator assigned to a case can see it. Every time a case is opened, it’s logged."
        actions={
          r.data?.canOpen && (
            <button className="btn primary" onClick={() => navigate(at('/ia/new'))}>
              New IA case
            </button>
          )
        }
      />
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which cases">
          {(
            [
              ['open', 'Open'],
              ['closed', 'Closed'],
              ['all', 'All'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
              {l}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Number, employee, complainant…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search IA cases" style={{ maxWidth: 260 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.cases.length ? (
        <EmptyState icon="check" title="No cases" />
      ) : (
        <div className="card table-wrap">
          <table className="table clickable">
            <thead>
              <tr>
                <th>Case</th>
                <th>Employees</th>
                <th>Source</th>
                <th>Investigator</th>
                <th>Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.cases.map((c: any) => (
                <tr key={c.id} onClick={() => navigate(at(`/ia/${c.id}`))}>
                  <td>
                    <strong className="mono">{c.number}</strong>
                    <div className="small muted">Received {day(c.received_on)}</div>
                  </td>
                  <td>{(c.employee_names ?? []).join(', ') || <span className="muted">—</span>}</td>
                  <td className="small">
                    {r.data.meta.sources[c.source]}
                    {c.complainant_name && <div className="muted">{c.complainant_name}</div>}
                  </td>
                  <td>{c.investigator_name ?? <Badge tone="warn">Not assigned</Badge>}</td>
                  <td className="small">{c.status === 'closed' ? `Closed ${day(c.closed_on)}` : c.due_on ? <span className={c.overdue ? 'bad-text' : ''}>{day(c.due_on)}{c.overdue ? ' · overdue' : ''}</span> : '—'}</td>
                  <td>
                    <Badge tone={STATUS_TONE[c.status]}>{r.data.meta.statuses[c.status]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type AllegIn = { id?: string; employeeId: string | null; text: string; policy: string | null; finding?: string | null };

function AllegationRows({ rows, setRows, people, employees }: { rows: AllegIn[]; setRows: (r: AllegIn[]) => void; people: Person[]; employees: string[] }) {
  const name = (id: string) => people.find((p) => p.id === id)?.name ?? '';
  return (
    <div className="stack">
      {rows.map((a, i) => (
        <div key={a.id ?? i} className="grid-3 ia-alleg">
          <label>
            Employee
            <select value={a.employeeId ?? ''} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, employeeId: e.target.value || null } : x)))}>
              <option value="">(not one person)</option>
              {employees.map((id) => (
                <option key={id} value={id}>
                  {name(id)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Allegation
            <input value={a.text} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} placeholder="e.g. Discourtesy" />
          </label>
          <div className="row gap-sm" style={{ alignItems: 'end' }}>
            <label style={{ flex: 1 }}>
              Policy
              <input value={a.policy ?? ''} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, policy: e.target.value || null } : x)))} placeholder="e.g. GO 2.04" />
            </label>
            <button type="button" className="btn sm ghost" aria-label="Remove allegation" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              ✕
            </button>
          </div>
        </div>
      ))}
      <div>
        <button type="button" className="btn sm" onClick={() => setRows([...rows, { employeeId: employees.length === 1 ? employees[0]! : null, text: '', policy: null }])}>
          Add an allegation
        </button>
      </div>
    </div>
  );
}

export function NewIaCase() {
  const { navigate } = useRouter();
  const bwc = new URLSearchParams(location.search).get('bwc');
  const r = useLoad(() => api.get<any>('/api/ia?status=open'));
  const a = useAction();
  const [f, setF] = useState({ receivedOn: new Date().toLocaleDateString('en-CA'), source: bwc ? 'bwc' : 'citizen', complainantName: '', complainantContact: '', summary: '', classification: 'formal', dueOn: '' });
  const [employees, setEmployees] = useState<string[]>([]);
  const [inv, setInv] = useState<string[]>([]);
  const [alleg, setAlleg] = useState<AllegIn[]>([]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { meta, people } = r.data;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="ops stack">
      <PageHeader crumb="Internal affairs" title="New IA case" sub={bwc ? 'From a body cam review referred to IA. The officer and what the supervisor wrote come over; leave the summary blank to use it.' : undefined} />
      <Card title="What came in">
        <div className="grid-3">
          <label>
            Received
            <input type="date" value={f.receivedOn} onChange={set('receivedOn')} />
          </label>
          <label>
            Source
            <select value={f.source} onChange={set('source')} disabled={!!bwc}>
              {Object.entries(meta.sources).map(([k, l]) => (
                <option key={k} value={k}>
                  {l as string}
                </option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select value={f.classification} onChange={set('classification')}>
              {Object.entries(meta.classes).map(([k, l]) => (
                <option key={k} value={k}>
                  {l as string}
                </option>
              ))}
            </select>
          </label>
          {f.source === 'citizen' && (
            <>
              <label>
                Complainant
                <input value={f.complainantName} onChange={set('complainantName')} />
              </label>
              <label>
                Complainant’s phone / email / address
                <input value={f.complainantContact} onChange={set('complainantContact')} />
              </label>
            </>
          )}
        </div>
        <label>
          Summary of the complaint
          <textarea rows={5} value={f.summary} onChange={set('summary')} placeholder={bwc ? 'Leave blank to use the body cam review' : 'What is alleged to have happened, when and where'} />
        </label>
      </Card>
      <Card title="Who it’s about">
        <PeoplePicker people={people} value={employees} onChange={setEmployees} label="Employees" placeholder={bwc ? 'The reviewed officer is added automatically' : 'Type a name…'} />
        <AllegationRows rows={alleg} setRows={setAlleg} people={people} employees={employees} />
      </Card>
      <Card title="Investigation">
        <div className="grid-3">
          <div>
            <PeoplePicker people={people.filter((p: Person) => !employees.includes(p.id))} value={inv} onChange={(v) => setInv(v.slice(-1))} label="Investigator" placeholder="Optional — assign later" />
          </div>
          <label>
            Due
            <input type="date" value={f.dueOn} onChange={set('dueOn')} />
          </label>
        </div>
      </Card>
      <ErrorBox error={a.error} />
      <div>
        <button
          className="btn primary"
          disabled={a.busy}
          onClick={() =>
            a.run(async () => {
              const res = await api.post<any>('/api/ia', { ...f, sourceRef: bwc, summary: f.summary || null, dueOn: f.dueOn || null, employees, allegations: alleg.filter((x) => x.text.trim()), investigatorId: inv[0] ?? null });
              navigate(at(`/ia/${res.id}`), true);
            })
          }
        >
          Open the case
        </button>
      </div>
    </div>
  );
}

function printNotice(d: any, type: string, employeeId: string, extra: { interviewAt?: string; place?: string }) {
  const c = d.case;
  const emp = d.people.find((p: any) => p.id === employeeId);
  const inv = d.people.find((p: any) => p.id === c.investigator_id);
  const mine = (c.allegations as any[]).filter((x) => !x.employeeId || x.employeeId === employeeId);
  const list = mine.map((x) => `<li>${esc(x.text)}${x.policy ? ` (${esc(x.policy)})` : ''}${type === 'outcome' ? ` — <strong>${esc(d.meta.findings[x.finding] ?? 'No finding')}</strong>` : ''}</li>`).join('');
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const body =
    type === 'investigation'
      ? `<p>This is to notify you that an internal investigation, case <strong>${esc(c.number)}</strong>, has been opened concerning the following allegation(s):</p><ul>${list}</ul>
         <p>${inv ? `${esc(inv.name)} is assigned to investigate.` : 'An investigator will be assigned.'} You will be told when you are to be interviewed. You are ordered not to discuss this investigation with anyone other than the investigator, your attorney or your representative, and not to contact the complainant or witnesses about it.</p>`
      : type === 'interview'
        ? `<p>You are ordered to appear for an interview in internal investigation <strong>${esc(c.number)}</strong>${extra.interviewAt ? ` on <strong>${esc(extra.interviewAt)}</strong>` : ''}${extra.place ? ` at <strong>${esc(extra.place)}</strong>` : ''}. The investigation concerns:</p><ul>${list}</ul>
           <p>You are required to answer questions truthfully and completely. Statements you give under this order cannot be used against you in a criminal proceeding (Garrity). You may have a representative present.</p>`
        : `<p>Internal investigation <strong>${esc(c.number)}</strong> is complete. The findings are:</p><ul>${list}</ul>${c.discipline && mine.some((x) => x.finding === 'sustained') ? `<p><strong>Action taken:</strong> ${esc(c.discipline)}</p>` : ''}<p>If you have questions about this notice, see the investigator or your chain of command.</p>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(d.meta.notices[type])} · ${esc(c.number)}</title>
    <style>body{font:12pt/1.5 Georgia,serif;max-width:7in;margin:.75in auto;color:#000}h1{font-size:15pt;text-align:center;margin:0}h2{font-size:13pt;text-align:center;margin:.2in 0}.meta td{padding:2px 10px 2px 0}.sig{margin-top:.6in;display:flex;gap:.6in}.sig div{flex:1;border-top:1px solid #000;padding-top:4px;font-size:10pt}.conf{text-align:center;font-size:9pt;letter-spacing:.1em}</style></head>
    <body><div class="conf">CONFIDENTIAL — INTERNAL AFFAIRS</div><h1>${esc(d.agencyName)}</h1><h2>${esc(d.meta.notices[type])}</h2>
    <table class="meta"><tr><td>To:</td><td>${esc(emp?.name)}${emp?.badge_number ? `, #${esc(emp.badge_number)}` : ''}</td></tr><tr><td>Case:</td><td>${esc(c.number)}</td></tr><tr><td>Date:</td><td>${today}</td></tr></table>
    ${body}
    <div class="sig"><div>Issued by</div><div>Date</div></div><div class="sig"><div>Received by employee</div><div>Date / time</div></div>
    <script>window.onload=()=>window.print()</script></body></html>`;
  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}

export function IaCaseDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/ia/${id}`), [id]);
  const a = useAction();
  const [edit, setEdit] = useState(false);
  const [alleg, setAlleg] = useState<AllegIn[] | null>(null);
  const [ev, setEv] = useState({ kind: 'interview', person: '', role: 'witness', text: '', recorded: false });
  const [nt, setNt] = useState({ type: 'investigation', employeeId: '', interviewAt: '', place: '' });
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const d = r.data;
  const c = d.case;
  const meta: Meta = d.meta;
  const people: any[] = d.people;
  const nameOf = (uid: string | null) => people.find((p) => p.id === uid)?.name ?? '—';
  const post = (path: string, body: unknown) => a.run(async () => (await api.post(`/api/ia/${id}${path}`, body), r.reload()));
  const al: AllegIn[] = alleg ?? c.allegations;
  const changedFindings = alleg != null;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Internal affairs"
        title={<span className="mono">{c.number}</span>}
        sub={`${meta.classes[c.classification]} · ${meta.sources[c.source]} · received ${day(c.received_on)}`}
        actions={
          <div className="row gap-sm" style={{ alignItems: 'center' }}>
            {c.status === 'closed' && <Badge tone={STATUS_TONE[c.status]}>{meta.statuses[c.status]}</Badge>}
            {c.status !== 'closed' && (
              <select aria-label="Status" value={c.status} disabled={a.busy} onChange={(e) => post('', { status: e.target.value })} style={{ width: 'auto' }}>
                {['open', 'investigating', 'review'].map((s) => (
                  <option key={s} value={s}>
                    {meta.statuses[s]}
                  </option>
                ))}
              </select>
            )}
            {d.canManage && c.status !== 'closed' && (
              <button
                className="btn primary"
                disabled={a.busy}
                onClick={() => {
                  const sustained = (c.allegations as any[]).some((x) => x.finding === 'sustained');
                  const disc = window.prompt(sustained ? 'Discipline given (required for a sustained finding):' : 'Discipline or other action (optional):', '');
                  if (disc === null) return;
                  post('/close', { discipline: disc || null });
                }}
              >
                Close case
              </button>
            )}
            {d.canManage && c.status === 'closed' && (
              <button
                className="btn"
                onClick={() => {
                  const reason = window.prompt('Why is the case being reopened?');
                  if (reason) post('/reopen', { reason });
                }}
              >
                Reopen
              </button>
            )}
          </div>
        }
      />
      <ErrorBox error={a.error} />
      {c.status === 'closed' && (
        <Notice tone="info">
          Closed {day(c.closed_on)} by {nameOf(c.closed_by)}.{c.discipline ? ` Action taken: ${c.discipline}` : ''}
        </Notice>
      )}
      <div className="grid-2-cards">
        <Card title="The complaint" actions={d.canManage && c.status !== 'closed' && <button className="btn sm ghost" onClick={() => setEdit(!edit)}>{edit ? 'Cancel' : 'Edit'}</button>}>
          {edit ? (
            <EditCase d={d} onDone={() => (setEdit(false), r.reload())} />
          ) : (
            <dl className="kv">
              {c.complainant_name && (
                <>
                  <dt>Complainant</dt>
                  <dd>
                    {c.complainant_name}
                    {c.complainant_contact && <div className="small muted">{c.complainant_contact}</div>}
                  </dd>
                </>
              )}
              <dt>Summary</dt>
              <dd style={{ whiteSpace: 'pre-wrap' }}>{c.summary}</dd>
              {c.source === 'bwc' && c.source_ref && (
                <>
                  <dt>From</dt>
                  <dd>
                    <a href={at(`/bwc/${c.source_ref}`)}>The body cam review</a>
                  </dd>
                </>
              )}
            </dl>
          )}
        </Card>
        <Card title="People">
          <dl className="kv">
            <dt>Employees</dt>
            <dd>
              {c.employees.length
                ? c.employees.map((uid: string) => {
                    const p = people.find((x) => x.id === uid);
                    return (
                      <div key={uid}>
                        <strong>{p?.name}</strong> <span className="small muted">{[p?.title, p?.call_number, p?.unit].filter(Boolean).join(' · ')}</span>
                      </div>
                    );
                  })
                : '—'}
            </dd>
            <dt>Investigator</dt>
            <dd>{c.investigator_id ? nameOf(c.investigator_id) : <Badge tone="warn">Not assigned</Badge>}</dd>
            <dt>Due</dt>
            <dd>{c.due_on ? day(c.due_on) : '—'}</dd>
            <dt>Opened by</dt>
            <dd className="small">{nameOf(c.created_by)}</dd>
          </dl>
        </Card>
      </div>

      <Card title={`Allegations and findings (${al.length})`}>
        {d.canManage && c.status !== 'closed' && alleg != null ? (
          <AllegationRows rows={al} setRows={setAlleg} people={people} employees={c.employees} />
        ) : !al.length ? (
          <p className="muted">No allegations yet.</p>
        ) : null}
        {al.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Allegation</th>
                <th>Finding</th>
              </tr>
            </thead>
            <tbody>
              {al.map((x, i) => (
                <tr key={x.id ?? i}>
                  <td>{x.employeeId ? nameOf(x.employeeId) : '—'}</td>
                  <td>
                    {x.text || <span className="muted">(new)</span>}
                    {x.policy && <div className="small muted">{x.policy}</div>}
                  </td>
                  <td>
                    {c.status === 'closed' ? (
                      x.finding ? <Badge tone={FINDING_TONE[x.finding]}>{meta.findings[x.finding]}</Badge> : '—'
                    ) : (
                      <select aria-label={`Finding: ${x.text}`} value={x.finding ?? ''} onChange={(e) => setAlleg(al.map((y, j) => (j === i ? { ...y, finding: e.target.value || null } : y)))}>
                        <option value="">No finding yet</option>
                        {Object.entries(meta.findings).map(([k, l]) => (
                          <option key={k} value={k}>
                            {l}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {c.status !== 'closed' && (
          <div className="row gap-sm">
            {changedFindings && (
              <>
                <button className="btn primary sm" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/ia/${id}/allegations`, { allegations: al.filter((x) => x.text.trim()) }), setAlleg(null), r.reload()))}>
                  Save allegations
                </button>
                <button className="btn sm ghost" onClick={() => setAlleg(null)}>
                  Cancel
                </button>
              </>
            )}
            {d.canManage && !changedFindings && (
              <button className="btn sm" onClick={() => setAlleg([...c.allegations])}>
                Add or change allegations
              </button>
            )}
          </div>
        )}
      </Card>

      {c.status !== 'closed' && (
        <Card title="Add an interview or note">
          <div className="seg-ctl sm" role="radiogroup" aria-label="Kind">
            {(
              [
                ['interview', 'Interview'],
                ['note', 'Note'],
              ] as const
            ).map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={ev.kind === k} className={ev.kind === k ? 'on' : ''} onClick={() => setEv({ ...ev, kind: k })}>
                {l}
              </button>
            ))}
          </div>
          {ev.kind === 'interview' && (
            <div className="grid-3">
              <label>
                Who was interviewed
                <input value={ev.person} onChange={(e) => setEv({ ...ev, person: e.target.value })} />
              </label>
              <label>
                As
                <select value={ev.role} onChange={(e) => setEv({ ...ev, role: e.target.value })}>
                  <option value="employee">Employee</option>
                  <option value="complainant">Complainant</option>
                  <option value="witness">Witness</option>
                </select>
              </label>
              <label className="check" style={{ alignSelf: 'end' }}>
                <input type="checkbox" checked={ev.recorded} onChange={(e) => setEv({ ...ev, recorded: e.target.checked })} /> Recorded
              </label>
            </div>
          )}
          <label>
            {ev.kind === 'interview' ? 'Summary of the interview' : 'Note'}
            <textarea rows={3} value={ev.text} onChange={(e) => setEv({ ...ev, text: e.target.value })} />
          </label>
          <div>
            <button className="btn" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/ia/${id}/events`, ev), setEv({ ...ev, person: '', text: '', recorded: false }), r.reload()))}>
              Add to the case
            </button>
          </div>
        </Card>
      )}

      <div className="grid-2-cards">
        <Card title="Notices to the employee">
          {!c.employees.length ? (
            <p className="muted small">Add the employees first.</p>
          ) : (
            <>
              <div className="grid-2">
                <label>
                  Notice
                  <select value={nt.type} onChange={(e) => setNt({ ...nt, type: e.target.value })}>
                    {Object.entries(meta.notices).map(([k, l]) => (
                      <option key={k} value={k} disabled={k === 'outcome' && c.status !== 'closed'}>
                        {l}
                        {k === 'outcome' && c.status !== 'closed' ? ' (after the case is closed)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  To
                  <select value={nt.employeeId} onChange={(e) => setNt({ ...nt, employeeId: e.target.value })}>
                    <option value="">Pick…</option>
                    {c.employees.map((uid: string) => (
                      <option key={uid} value={uid}>
                        {nameOf(uid)}
                      </option>
                    ))}
                  </select>
                </label>
                {nt.type === 'interview' && (
                  <>
                    <label>
                      Date and time
                      <input value={nt.interviewAt} onChange={(e) => setNt({ ...nt, interviewAt: e.target.value })} placeholder="e.g. Oct 20 at 10:00 AM" />
                    </label>
                    <label>
                      Place
                      <input value={nt.place} onChange={(e) => setNt({ ...nt, place: e.target.value })} placeholder="e.g. Sheriff’s office, IA office" />
                    </label>
                  </>
                )}
              </div>
              <div>
                <button
                  className="btn"
                  disabled={a.busy || !nt.employeeId}
                  onClick={() =>
                    a.run(async () => {
                      await api.post(`/api/ia/${id}/notice`, nt);
                      printNotice(d, nt.type, nt.employeeId, nt);
                      r.reload();
                    })
                  }
                >
                  Print notice
                </button>
              </div>
              <p className="small muted">Printing is logged on the case. Have the employee sign the copy you keep and add it under Files.</p>
            </>
          )}
        </Card>
        <Card title={`Files (${d.files.length})`} actions={c.status !== 'closed' && <UploadButton url={`/api/ia/${id}/files`} onDone={r.reload} />}>
          <FileList files={d.files} />
        </Card>
      </div>

      <Card title="Case history">
        {!d.events.length ? (
          <p className="muted">Nothing yet.</p>
        ) : (
          <ul className="plain ia-history">
            {d.events.map((e: any) => (
              <li key={e.id}>
                <span className="small muted">{when(e.at)} · {e.by_name}</span>
                <div>{eventText(e, meta, nameOf)}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <details className="card ia-views">
        <summary>
          Who has opened this case ({d.viewCount} {d.viewCount === 1 ? 'time' : 'times'})
        </summary>
        <ul className="plain small">
          {d.views.map((v: any, i: number) => (
            <li key={i}>
              {when(v.at)} · {v.by}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function eventText(e: any, meta: Meta, nameOf: (id: string | null) => string) {
  const x = e.data ?? {};
  switch (e.kind) {
    case 'opened':
      return <strong>Case opened ({meta.sources[x.source] ?? x.source})</strong>;
    case 'assigned':
      return x.investigatorId ? `Assigned to ${nameOf(x.investigatorId)}` : 'Investigator removed';
    case 'status':
      return `Status: ${meta.statuses[x.from] ?? x.from} → ${meta.statuses[x.to] ?? x.to}${x.reason ? ` — ${x.reason}` : ''}`;
    case 'finding':
      return (
        <>
          Finding on “{x.allegation}”{x.employeeId ? ` (${nameOf(x.employeeId)})` : ''}: <Badge tone={FINDING_TONE[x.finding]}>{meta.findings[x.finding]}</Badge>
        </>
      );
    case 'interview':
      return (
        <>
          <strong>
            Interview: {x.person} ({x.role}
            {x.recorded ? ', recorded' : ''})
          </strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{x.text}</div>
        </>
      );
    case 'note':
      return <div style={{ whiteSpace: 'pre-wrap' }}>{x.text}</div>;
    case 'notice':
      return `${meta.notices[x.type] ?? 'Notice'} printed for ${nameOf(x.employeeId)}`;
    case 'discipline':
      return <strong>Discipline: {x.text}</strong>;
    case 'file':
      return `File added: ${x.name}`;
    default:
      return e.kind;
  }
}

function EditCase({ d, onDone }: { d: any; onDone: () => void }) {
  const c = d.case;
  const [f, setF] = useState({ summary: c.summary, classification: c.classification, complainantName: c.complainant_name ?? '', complainantContact: c.complainant_contact ?? '', dueOn: c.due_on ?? '' });
  const [employees, setEmployees] = useState<string[]>(c.employees);
  const [inv, setInv] = useState<string[]>(c.investigator_id ? [c.investigator_id] : []);
  const a = useAction();
  return (
    <div className="stack">
      <label>
        Type
        <select value={f.classification} onChange={(e) => setF({ ...f, classification: e.target.value })}>
          {Object.entries(d.meta.classes).map(([k, l]) => (
            <option key={k} value={k}>
              {l as string}
            </option>
          ))}
        </select>
      </label>
      <div className="grid-2">
        <label>
          Complainant
          <input value={f.complainantName} onChange={(e) => setF({ ...f, complainantName: e.target.value })} />
        </label>
        <label>
          Contact
          <input value={f.complainantContact} onChange={(e) => setF({ ...f, complainantContact: e.target.value })} />
        </label>
      </div>
      <label>
        Summary
        <textarea rows={5} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} />
      </label>
      <PeoplePicker people={d.staff} value={employees} onChange={setEmployees} label="Employees" />
      <PeoplePicker people={d.staff.filter((p: Person) => !employees.includes(p.id))} value={inv} onChange={(v) => setInv(v.slice(-1))} label="Investigator" />
      <label>
        Due
        <input type="date" value={f.dueOn} onChange={(e) => setF({ ...f, dueOn: e.target.value })} />
      </label>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/ia/${c.id}`, { ...f, complainantName: f.complainantName || null, complainantContact: f.complainantContact || null, dueOn: f.dueOn || null, employees, investigatorId: inv[0] ?? null }), onDone()))}>
          Save
        </button>
      </div>
    </div>
  );
}
